/** Business Files — v1's businessFilesPanel as a page: dark hero with the
 * connection pill + action row (Refresh live; Connect/Disconnect/New Folder/
 * Upload disabled until Phase B), breadcrumb, search, folder/file grid.
 * Read-only by design in Phase A. */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  BIZ_MSG, bizFilter, crumbTo,
  type BizCrumb, type BizItem, type BizStatus,
} from "./api";

export interface BizLoaders {
  status: () => Promise<boolean>;
  folder: (folderId: string | null, onWake?: () => void) => Promise<{ status: BizStatus; folder: { folderId: string | null; items: BizItem[] } }>;
}

export function BizFilesPage({ loaders }: { loaders: BizLoaders }) {
  const [connected, setConnected] = useState<boolean | null>(null); // null = checking
  const [items, setItems] = useState<BizItem[]>([]);
  const [path, setPath] = useState<BizCrumb[]>([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const gen = useRef(0);

  const load = useCallback(
    async (folderId: string | null, nextPath: BizCrumb[]) => {
      const g = ++gen.current;
      setBusy(true);
      setNote(null);
      const r = await loaders.folder(folderId, () => {
        if (gen.current === g) setNote("Waking the backend — one moment…");
      });
      if (gen.current !== g) return; // a newer click superseded this load
      setBusy(false);
      if (r.status === "disconnected") {
        setConnected(false);
        setItems([]);
        setPath([]);
        setNote(BIZ_MSG.expired);
        return;
      }
      if (r.status === "transient") {
        setNote(BIZ_MSG.transient);
        return;
      }
      setConnected(true);
      setItems(r.folder.items);
      setPath(nextPath);
      setNote(null);
    },
    [loaders],
  );

  useEffect(() => {
    let alive = true;
    void loaders.status().then((ok) => {
      if (!alive) return;
      setConnected(ok);
      if (ok) void load(null, []);
      else setNote(BIZ_MSG.expired);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openFolder = (it: BizItem) => {
    if (it.kind !== "folder") return;
    void load(it.id, [...path, { id: it.id, name: it.name }]);
  };
  const openCrumb = (i: number) => {
    const c = crumbTo(path, i);
    void load(c.folderId, c.path);
  };

  const shown = bizFilter(items, q);

  return (
    <div className="biz">
      <div className="anlhero">
        <div className="anlherotext">
          <h1>{BIZ_MSG.title}</h1>
          <p className={connected ? "bizok" : ""}>
            {connected === null ? "Checking Google Drive…" : connected ? "✓ " + BIZ_MSG.connected : BIZ_MSG.notConnected}
          </p>
        </div>
        <div className="bizacts">
          <button className="btn bizbtn" onClick={() => void load(path.length ? path[path.length - 1]!.id : null, path)} disabled={busy || connected === false}>
            {busy ? "Refreshing…" : "⟳ Refresh"}
          </button>
          <button className="btn bizbtn" disabled title={BIZ_MSG.phaseB}>🔗 {connected ? "Disconnect" : "Connect Google Drive"}</button>
          <button className="btn bizbtn" disabled title={BIZ_MSG.phaseB}>📁 New Folder</button>
          <button className="btn bizbtn primary" disabled title={BIZ_MSG.phaseB}>⬆ Upload</button>
        </div>
      </div>

      <div className="bizphaseb">{BIZ_MSG.phaseB}</div>
      {note ? <div className="pill stale">{note}</div> : null}

      <div className="bizsearch">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search files and folders..."
        />
      </div>

      <section className="bizcard">
        <div className="bizcardhead">
          <h3>{path.length ? path[path.length - 1]!.name : "Business Files"}</h3>
          <span className="lbadge">{items.length} items</span>
        </div>
        {path.length ? (
          <div className="bizcrumbs">
            <button onClick={() => openCrumb(-1)}>Business Files</button>
            {path.map((p, i) => (
              <span key={p.id}>
                <span className="bizsep">/</span>
                <button className={i === path.length - 1 ? "cur" : ""} onClick={() => openCrumb(i)}>
                  {p.name}
                </button>
              </span>
            ))}
          </div>
        ) : null}
        <div className="bizgrid">
          {shown.length ? (
            shown.map((it) => (
              <button
                key={it.id}
                className="bizitem"
                onClick={() => {
                  if (it.kind === "folder") openFolder(it);
                  else if (it.link) window.open(it.link, "_blank", "noopener");
                }}
              >
                <span className={"bizico " + it.kind}>{it.kind === "folder" ? "📁" : "📄"}</span>
                <span className="bizmeta">
                  <span className="bizname">{it.name}</span>
                  <span className="bizsub">{it.kind === "folder" ? BIZ_MSG.folderSub : BIZ_MSG.fileSub}</span>
                </span>
                <span className="bizarrow">{it.kind === "folder" ? "›" : "↗"}</span>
              </button>
            ))
          ) : (
            <div className="bizempty">{connected ? BIZ_MSG.empty : "—"}</div>
          )}
        </div>
      </section>

      <p className="anldisclaimer">{BIZ_MSG.note}</p>
    </div>
  );
}
